import { Timestamp } from "firebase/firestore";
import type { WizardDocRequirementId } from "../../shared-utils/complianceConstants";
import type { VocationId } from "../../shared-utils/vocationId";

export {
  CDL_VOCATION_ID,
  isCdl,
  isCdlVocation,
  isNonCdlVocation,
  isVocationId,
  parseVocationId,
  resolveVocationId,
  VOCATION_ID_PATTERN,
} from "../../shared-utils/vocationId";
export type { VocationId } from "../../shared-utils/vocationId";

// Existing CDL exam/TPR configuration (mirrors functions/VocationTypes.ts).
export type ManualSectionConfig = {
  sectionNumber: string;
  name: string;
};

export type ELDTSectionRefConfig = {
  code: string;
  name: string;
};

export type CDLCategoryConfig = {
  id: string;
  name: string;
  examSize: number;
  manualSections: ManualSectionConfig[];
  eldtSections: ELDTSectionRefConfig[];
};

export type ELDTTopicConfig = {
  code: string;
  name: string;
  sectionCode: string;
  sectionName: string;
};

export type TPRConfigType = {
  minVideosStarted: number;
  minAverageScore: number;
  requiredCategories: string[];
};

export type CDLVocationConfigType = {
  categories: CDLCategoryConfig[];
  eldtTopics: ELDTTopicConfig[];
  tprConfig: TPRConfigType;
};

// ─── Curriculum (mirrors functions/types/VocationCurriculumConfig.ts) ─
// The ordered learning structure (course → units → lessons) stored inline on
// the vocation, replacing the DatoCMS resource view. `type` selects the player;
// every lesson carries the source video's media metadata for the unit card and
// progress tracking.

export const CURRICULUM_LESSON_TYPES = [
  "video",
  "practice",
  "trailer",
] as const;

export type CurriculumLessonType = typeof CURRICULUM_LESSON_TYPES[number];

export type CurriculumQuizRef = {
  id: string;
  name: string;
  quizType: string;
};

type CurriculumLessonBase = {
  /** REUSES the DatoCMS resource id so existing progress docs keep matching. */
  id: string;
  title: string;
  order: number;
  muxPlaybackId: string;
  /**
   * Minutes. Carried over verbatim from the source DatoCMS resource duration
   * and consumed as minutes (useVideoProgress multiplies by 60; the unit cards
   * render "{duration} min").
   */
  duration: number;
  thumbnailUrl?: string;
  quiz?: CurriculumQuizRef;
};

export type VideoLesson = CurriculumLessonBase & {
  type: "video";
  fallbackUrl?: string;
  description?: string;
  /** DatoCMS structured-text (DAST) summary, rendered via react-datocms. */
  summary?: unknown;
};

export type PracticeLesson = CurriculumLessonBase & {
  type: "practice";
  /** References a local study guide (loadChapter); content is not stored here. */
  studyGuideId: string;
};

export type TrailerLesson = CurriculumLessonBase & {
  type: "trailer";
};

export type CurriculumLesson = VideoLesson | PracticeLesson | TrailerLesson;

export type CurriculumUnit = {
  id: string;
  name: string;
  order: number;
  thumbnailUrl?: string;
  lessons: CurriculumLesson[];
};

export type VocationCurriculum = {
  sourceCourseId?: string;
  units: CurriculumUnit[];
};

export const VOCATION_SURVEY_STEPS = [
  "personal",
  "financial",
  "driving",
  "criminal",
  "medical",
  "cdl_vocational",
  "trade_questions",
] as const;

export type VocationSurveyStep = typeof VOCATION_SURVEY_STEPS[number];

export const TRADE_QUESTION_TYPES = ["text", "select", "checkbox"] as const;

export type TradeQuestionType = typeof TRADE_QUESTION_TYPES[number];

/** Plain text; line breaks and bullets are preserved without a rich-text DSL. */
export type TradeQuestion = {
  key: string;
  label: string;
  /** Optional visual grouping for long, data-driven questionnaires. */
  section?: string;
  /** Plain-text context rendered above this question; newlines are preserved. */
  description?: string;
  /**
   * Optional two-letter state codes that control where this question applies.
   * Questions without this field are shared across every program location.
   */
  applicableStates?: string[];
  type: TradeQuestionType;
  options?: string[];
  required: boolean;
};

export type VocationApplicationConfig = {
  steps: VocationSurveyStep[];
  tradeQuestions: TradeQuestion[];
  autoDecision: boolean;
  copyOverrides: Record<string, string>;
  interviewQuestions?: Record<string, string>;
};

export const VOCATION_WIZARD_SEGMENTS = [
  "splash",
  "checklist_overview",
  "documents",
  "background_check",
  "references",
  "enrollment_documents",
  "fcra_authorization",
  "tpr_compliance",
  "schedule_meetings",
  "your_story",
  "completion_celebration",
  "week_zero_handoff",
] as const;

export type VocationWizardSegment = typeof VOCATION_WIZARD_SEGMENTS[number];

export const VOCATION_ENTRY_REQUIREMENTS = [
  "permit_verified",
  "conditionally_enrolled",
] as const;

export type VocationEntryRequirement =
  typeof VOCATION_ENTRY_REQUIREMENTS[number];

export const VOCATION_MOTIVATOR_SETS = ["cdl", "generic"] as const;

export type VocationMotivatorSet = typeof VOCATION_MOTIVATOR_SETS[number];

export const VOCATION_APPOINTMENT_TYPES = [
  "enrollment-interview",
  "week-0-assessment",
] as const;

export type VocationAppointmentType = typeof VOCATION_APPOINTMENT_TYPES[number];

export const CALENDAR_PROVIDERS = ["cal.com"] as const;

export type CalendarProvider = typeof CALENDAR_PROVIDERS[number];

export type VocationAppointment = {
  type: VocationAppointmentType;
  calendarProvider: CalendarProvider;
  name: string;
  slug: string;
  path: string;
};

export type VocationEnrollmentConfig = {
  requiredDocs: WizardDocRequirementId[];
  wizardSegments: VocationWizardSegment[];
  entryRequirement: VocationEntryRequirement;
  motivatorSet: VocationMotivatorSet;
  appointments: VocationAppointment[];
  copyOverrides: Record<string, string>;
};

export type Vocation = {
  id: VocationId;
  name: string;
  shortName: string;
  description?: string;
  isActive: boolean;
  config?: CDLVocationConfigType;
  curriculum?: VocationCurriculum;
  application?: VocationApplicationConfig;
  enrollment?: VocationEnrollmentConfig;
  createdAt: string;
  createdAtFirestoreTimestamp: Timestamp;
  lastUpdatedAt: string;
  lastUpdatedAtFirestoreTimestamp: Timestamp;
};

export type PublicVocation = Pick<
  Vocation,
  "id" | "name" | "shortName" | "description" | "isActive"
>;
